import type { ProjectDetailsStoryboardShot, ProjectDetailsVisual } from "@/types/project";
import { userVideoGenPromptFromStoredPrompt } from "@/services/generation-prompts";
import { parseUploadedImages } from "@/services/project-entity";
import { isOmniFamilyVideoModel } from "@/utils/videoExtend";
import { preferredVideoIdFromStoryboardShotImages, storyboardShotMainVideoId } from "@/services/storyboardDisplayedImage";

function completedVisualForPreferredVideoId(
  shot: ProjectDetailsStoryboardShot,
  preferredVideoId: string | null | undefined
): ProjectDetailsVisual | undefined {
  if (typeof preferredVideoId !== "string" || preferredVideoId === "") {
    return undefined;
  }

  return (shot.visuals ?? []).find((v) => v.status === "completed" && v.video_id === preferredVideoId);
}

/**
 * True when the shot-level working copy is verbatim another completed video's prompt, i.e. left behind
 * by a later generation rather than typed by the user — the one case where it may be ignored.
 */
function shotVideoGenPromptBelongsToOtherVisual(
  shot: ProjectDetailsStoryboardShot,
  displayedVideoId: string | null | undefined
): boolean {
  const fromShot = shot["video-gen-prompt"]?.trim() ?? "";
  if (fromShot.length === 0) {
    return false;
  }

  const displayed = typeof displayedVideoId === "string" ? displayedVideoId.trim() : "";
  const visuals = shot.visuals ?? [];

  const displayedVisual = visuals.find((v) => v.status === "completed" && (v.video_id?.trim() ?? "") === displayed);
  if (displayedVisual !== undefined && userVideoGenPromptFromStoredPrompt(displayedVisual.prompt) === fromShot) {
    return false;
  }

  return visuals.some((v) => {
    const vid = v.video_id?.trim() ?? "";
    if (v.status !== "completed" || vid === "" || vid === displayed) {
      return false;
    }

    return userVideoGenPromptFromStoredPrompt(v.prompt) === fromShot;
  });
}

/** User-facing description for the Video panel: shot field first, then the active visual's stored prompt. */
export function resolveVideoPanelDescription(
  shot: ProjectDetailsStoryboardShot | null | undefined,
  preferredVideoId?: string | null
): string {
  if (shot === undefined || shot === null) {
    return "";
  }

  const videoId = preferredVideoId ?? preferredVideoIdFromStoryboardShotImages(shot);
  const visual = completedVisualForPreferredVideoId(shot, videoId);
  const fromVisual = userVideoGenPromptFromStoredPrompt(visual?.prompt);
  const fromShot = shot["video-gen-prompt"]?.trim() ?? "";

  if (fromShot.length > 0 && !(fromVisual.length > 0 && shotVideoGenPromptBelongsToOtherVisual(shot, videoId))) {
    return fromShot;
  }

  return fromVisual;
}

/**
 * Reference images for the Video panel. Reconciles stale `video-uploaded-images` on the shot
 * with the active completed visual (Omni text-only runs often leave both fields inconsistent).
 *
 * For completed Omni videos, project JSON is the working copy for the next run. Video-entity
 * `parent_ids` fill in when project JSON is a stale strict prefix (partial snapshot after generation).
 * When metadata has loaded and `parent_ids` is empty, the video was text-only — show no refs.
 * When the shot has no video, the approved timeline still is auto-populated when present.
 */
function isStalePartialOmniUpload(
  uploaded: readonly string[],
  metadataParents: readonly string[]
): boolean {
  return (
    metadataParents.length > uploaded.length &&
    uploaded.every((id, index) => id === metadataParents[index])
  );
}

/** The working copy still accounts for this video's references, so extra ones the user added must survive. */
function uploadedCoversVideoReferences(uploaded: readonly string[], videoReferences: readonly string[]): boolean {
  return videoReferences.length > 0 && videoReferences.every((id) => uploaded.includes(id));
}

export function resolveVideoPanelReferenceImageIds(
  shot: ProjectDetailsStoryboardShot | null | undefined,
  preferredVideoId?: string | null,
  videoMetadataParentIds?: readonly string[],
  omniMetadataSettled = false
): readonly string[] {
  if (shot === undefined || shot === null) {
    return [];
  }

  const uploaded = parseUploadedImages(shot["video-uploaded-images"]);
  const metadataParents = parseUploadedImages(videoMetadataParentIds);
  const mainVideoId = storyboardShotMainVideoId(shot);

  if (mainVideoId === null) {
    const approved0 = shot["approved-timeline-images"]?.[0]?.trim() ?? "";
    if (approved0 !== "") {
      return [approved0];
    }

    return uploaded;
  }

  const approvedFrame = shot["approved-timeline-images"]?.[0]?.trim() ?? "";
  if (approvedFrame !== "" && uploaded[0] === approvedFrame) {
    return uploaded;
  }

  const videoId = preferredVideoId ?? preferredVideoIdFromStoryboardShotImages(shot) ?? mainVideoId;
  const visual = completedVisualForPreferredVideoId(shot, videoId);
  if (visual === undefined) {
    return uploaded;
  }

  const refId = visual.reference_image_id?.trim() ?? "";
  if (isOmniFamilyVideoModel(visual.model)) {
    if (!omniMetadataSettled) {
      return [];
    }

    if (metadataParents.length > 0) {
      if (
        uploaded.length > 0 &&
        !isStalePartialOmniUpload(uploaded, metadataParents) &&
        (uploadedCoversVideoReferences(uploaded, metadataParents) ||
          !shotVideoGenPromptBelongsToOtherVisual(shot, videoId))
      ) {
        return uploaded;
      }

      return metadataParents;
    }

    return [];
  }

  if (
    uploaded.length > 0 &&
    (uploadedCoversVideoReferences(uploaded, refId === "" ? [] : [refId]) ||
      !shotVideoGenPromptBelongsToOtherVisual(shot, videoId))
  ) {
    return uploaded;
  }

  return refId === "" ? [] : [refId];
}

/** Ordered reference ids for video generation UI: approved keyframe first unless the uploads already carry it. */
export function buildVideoDisplayReferenceImageIds(args: {
  readonly primaryImageId: string | null;
  readonly uploadedImageIds: readonly string[];
  readonly primaryExcluded: boolean;
}): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  const add = (id: string | null | undefined) => {
    if (typeof id !== "string" || id === "" || seen.has(id)) {
      return;
    }

    seen.add(id);
    ids.push(id);
  };

  const uploadedHasPrimary =
    typeof args.primaryImageId === "string" && parseUploadedImages(args.uploadedImageIds).includes(args.primaryImageId);
  if (!args.primaryExcluded && !uploadedHasPrimary) {
    add(args.primaryImageId);
  }

  for (const id of args.uploadedImageIds) {
    add(id);
  }

  return ids;
}
